import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  ChatProvider,
  ChatRoot,
  ChatHeader,
  ChatMessageList,
  ChatInput,
  ChatConnectionStatus,
  ChatSendError,
  ChatFloatingWrapper,
  useChatContext,
  registerCustomElement,
  type CustomUIComponent,
} from '@almai1/chatbot-widget'

// Import shared card components and locale sync
import {
  FlightCard,
  HotelCard,
  TourActivityCard,
  LeadForm,
  useLocaleSync,
  invalidatePersistedSessionOnVersionChange,
} from '@local/shared-cards'

// Wrapper for LeadForm that injects context values
const LeadFormWrapper: React.FC<{ data?: any }> = ({ data }) => {
  const { sessionId, sessionKey, config, handleSseEvent } = useChatContext()
  return (
    <LeadForm
      data={data}
      apiBaseUrl={config.apiBaseUrl}
      sessionId={sessionId}
      sessionKey={sessionKey}
      onSseEvent={handleSseEvent}
    />
  )
}

// Register custom UI elements (cast to CustomUIComponent as they accept specific data types)
registerCustomElement('flight_card', FlightCard as CustomUIComponent)
registerCustomElement('hotel_card', HotelCard as CustomUIComponent)
registerCustomElement('tour_activity_card', TourActivityCard as CustomUIComponent)
registerCustomElement('lead_form', LeadFormWrapper as CustomUIComponent)

// Default production API URL
const DEFAULT_API_URL = 'https://travel-assistant-superviaggi-api.almai-service.com/api'

const WIDGET_COPY = {
  it: {
    headerTitle: 'Chatta con noi',
    headerSubtitle: 'Siamo qui per aiutarti',
    welcomeTitle: 'Benvenuto in Superviaggi',
    welcomeMessage: 'Raccontami il viaggio che hai in mente: luna di miele, Maldive, tour, voli o un itinerario su misura.',
    inputPlaceholder: 'Chiedi informazioni su voli, hotel o viaggi...',
    launcherLabel: 'Pianifica il tuo viaggio',
  },
  en: {
    headerTitle: 'Chat with us',
    headerSubtitle: "We're here to help",
    welcomeTitle: 'Welcome to Superviaggi',
    welcomeMessage: 'Tell me about the trip you have in mind: honeymoon, Maldives, tours, flights, or a tailored itinerary.',
    inputPlaceholder: 'Ask about flights, hotels, or travel plans...',
    launcherLabel: 'Plan your trip',
  },
}

export type WidgetMode = 'embedded' | 'popup'
type WidgetLanguage = keyof typeof WIDGET_COPY

function getWidgetCopy(language?: string) {
  const lang = language?.split('-')[0] as WidgetLanguage | undefined
  return lang && lang in WIDGET_COPY ? WIDGET_COPY[lang] : WIDGET_COPY.it
}

// Component that syncs i18n with session locale
function LocaleSync() {
  const { locale } = useChatContext()
  useLocaleSync(locale)
  return null
}

// Hook to handle mobile keyboard viewport changes
function useKeyboardViewport() {
  useEffect(() => {
    // Only run on mobile devices with visualViewport support
    if (typeof window === 'undefined' || !window.visualViewport) return

    const viewport = window.visualViewport
    let initialHeight = viewport.height

    const handleResize = () => {
      // Calculate keyboard height (difference from initial viewport)
      const keyboardHeight = initialHeight - viewport.height

      // Apply CSS variable for keyboard offset (positive when keyboard is open)
      if (keyboardHeight > 100) {
        // Keyboard is likely open (threshold to avoid false positives)
        document.documentElement.style.setProperty('--keyboard-offset', `${keyboardHeight}px`)
      } else {
        document.documentElement.style.setProperty('--keyboard-offset', '0px')
      }
    }

    // Update initial height on orientation change
    const handleOrientationChange = () => {
      setTimeout(() => {
        initialHeight = viewport.height
        handleResize()
      }, 100)
    }

    viewport.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleOrientationChange)

    return () => {
      viewport.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleOrientationChange)
      document.documentElement.style.removeProperty('--keyboard-offset')
    }
  }, [])
}

function RestartButton() {
  const { t } = useTranslation()
  const { createNewSession, isTyping } = useChatContext()

  const handleRestart = async () => {
    if (confirm(t('widget.restartConfirm'))) {
      await createNewSession()
    }
  }

  return (
    <button
      className="widget-restart-btn"
      onClick={handleRestart}
      disabled={isTyping}
      title={t('widget.restartButton')}
      aria-label={t('widget.restartButton')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 4v6h6" />
        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
      </svg>
    </button>
  )
}

function CloseButton() {
  const { setIsOpen } = useChatContext()
  const { t } = useTranslation()

  return (
    <button
      className="widget-close-btn"
      onClick={() => setIsOpen(false)}
      title={t('widget.closeButton', 'Close')}
      aria-label={t('widget.closeButton', 'Close')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6L6 18" />
        <path d="M6 6l12 12" />
      </svg>
    </button>
  )
}

// Unified activity indicator: replaces both typing dots and tool status
function ActivityIndicator() {
  const { isTyping, isTranscribing, timeline } = useChatContext()

  // Find the latest tool status message from the timeline
  const toolStatus = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      const item = timeline[i] as any
      if (item.type === 'toolStatus') {
        return item.data?.statusMessage as string | undefined
      }
    }
    return undefined
  }, [timeline])

  if (!isTyping && !isTranscribing) return null

  return (
    <div className="widget-activity-indicator">
      {toolStatus ? (
        <span className="activity-indicator-content">{toolStatus}</span>
      ) : (
        <span className="activity-indicator-dots">
          <span className="activity-dot" />
          <span className="activity-dot" />
          <span className="activity-dot" />
        </span>
      )}
    </div>
  )
}

// Input area with error handling
function ChatInputWithError() {
  const { t } = useTranslation()
  const [restoredValue, setRestoredValue] = useState<string | undefined>(undefined)

  // Auto-restore message to input when error occurs
  const handleError = useCallback((content: string | null) => {
    if (content) {
      setRestoredValue(content)
    }
  }, [])

  const handleRestoredValueApplied = useCallback(() => {
    setRestoredValue(undefined)
  }, [])

  return (
    <div className="widget-input-area">
      <ChatSendError
        className="widget-send-error"
        messageClassName="widget-send-error-message"
        closeButtonClassName="widget-send-error-close"
        progressClassName="widget-send-error-progress"
        errorMessage={t('widget.sendFailed')}
        onError={handleError}
      />
      <ChatInput
        className="widget-input-container"
        formClassName="widget-input-form"
        textareaClassName="widget-textarea"
        sendButtonClassName="widget-send-btn"
        audioButtonClassName="widget-audio-btn"
        restoredValue={restoredValue}
        onRestoredValueApplied={handleRestoredValueApplied}
        sendButtonContent={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        }
        audioButtonContent={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        }
        audioRecordingContent={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2"/>
          </svg>
        }
      />
    </div>
  )
}

function ChatContent({ mode = 'embedded' }: { mode?: WidgetMode }) {
  const { t, i18n } = useTranslation()
  const { setIsOpen } = useChatContext()
  const copy = getWidgetCopy(i18n.language)

  // Close popup on Escape key
  useEffect(() => {
    if (mode !== 'popup') return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mode, setIsOpen])

  return (
    <ChatRoot className="widget-root">
      <ChatHeader className="widget-header">
        <div className="widget-header-content">
          <div className="widget-header-logo">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div className="widget-header-text">
            <h1>{copy.headerTitle}</h1>
            <p>{copy.headerSubtitle}</p>
          </div>
        </div>
        <div className="widget-header-actions">
          <RestartButton />
          {mode === 'popup' && <CloseButton />}
          <ChatConnectionStatus
            className="widget-connection-status"
            showWhenReady={false}
            noSessionText={t('widget.offline')}
          />
        </div>
      </ChatHeader>

      <ChatMessageList
        className="widget-messages"
        emptyState={
          <div className="widget-empty-state">
            <div className="widget-empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <h3>{copy.welcomeTitle}</h3>
            <p>{copy.welcomeMessage}</p>
          </div>
        }
      />

      <ActivityIndicator />

      <ChatInputWithError />
    </ChatRoot>
  )
}

function PopupLauncherContent() {
  const { i18n } = useTranslation()
  const copy = getWidgetCopy(i18n.language)

  return (
    <>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        <path d="M8 9h8M8 13h4"/>
      </svg>
      <span className="widget-popup-launcher-label">{copy.launcherLabel}</span>
    </>
  )
}

function ChatWidgetInner({ mode = 'embedded' }: { mode?: WidgetMode }) {
  if (mode === 'popup') {
    return (
      <>
        <LocaleSync />
        <ChatFloatingWrapper
          position="bottom-right"
          offset={{ x: 20, y: 20 }}
          launcherClassName="widget-popup-launcher"
          panelClassName="widget-popup-panel"
          closeButtonClassName="widget-popup-close"
          showCloseButton={false}
          launcherContent={<PopupLauncherContent />}
        >
          <ChatContent mode="popup" />
        </ChatFloatingWrapper>
      </>
    )
  }

  return (
    <>
      <LocaleSync />
      <ChatContent mode="embedded" />
    </>
  )
}

interface ChatWidgetProps {
  /** Override the API URL (defaults to production) */
  apiUrl?: string
  /** Widget display mode */
  mode?: WidgetMode
}

export function ChatWidget({ apiUrl, mode = 'embedded' }: ChatWidgetProps) {
  const { i18n } = useTranslation()
  // Priority: prop > default (env var only works in dev mode)
  const baseUrl = apiUrl || DEFAULT_API_URL
  const copy = getWidgetCopy(i18n.language)

  invalidatePersistedSessionOnVersionChange()

  // Handle mobile keyboard viewport changes
  useKeyboardViewport()

  return (
    <ChatProvider
      config={{
        apiBaseUrl: baseUrl,
        persistSession: true,
        sessionTTL: 8,
        enableAudio: true,
        showTimestamps: true,
        showToolStatus: false,
        placeholder: copy.inputPlaceholder,
      }}
    >
      <ChatWidgetInner mode={mode} />
    </ChatProvider>
  )
}
